import type { AABB } from '../pc/colliders'
import { MAX_STEP, moveAndResolveCollisions } from '../pc/colliders'
import {
  BOARD_ISLAND_Z,
  BOARD_INTERIOR,
  CAFE_ISLAND_Z,
  CAFE_INTERIOR,
  DATACENTRE_ISLAND_Z,
  DATACENTRE_INTERIOR,
  EXCHANGE_ISLAND_Z,
  EXCHANGE_INTERIOR,
  LIBRARY_ISLAND_Z,
  LIBRARY_INTERIOR,
  OPS_ISLAND_Z,
  OPS_INTERIOR,
  WORKSHOP_ISLAND_Z,
  WORKSHOP_INTERIOR,
  type InteriorDef,
} from '../pc/world/interiors'

export interface WorkplaceTraversalSpec {
  plotId: 'P1' | 'P2' | 'P4' | 'P5' | 'P6' | 'P7' | 'P8'
  purpose: string
  interior: InteriorDef
  islandZ: number
  /** Local-space waypoints from spawn to the useful room surface. */
  waypoints: readonly (readonly [number, number])[]
}

export const WORKPLACE_TRAVERSAL_SPECS: readonly WorkplaceTraversalSpec[] = [
  {
    plotId: 'P1',
    purpose: 'decision lounge, strategy suite, review nook, and task wall',
    interior: BOARD_INTERIOR,
    islandZ: BOARD_ISLAND_Z,
    // Visit every Board program, enter and leave the glass suite through its
    // real 1.5 m door, then use the clear east lane to reach the live wall.
    waypoints: [
      [-7, 4.6],
      [0, 4.8],
      [0, 3.5],
      [0, 4.8],
      [7.2, 4.6],
      [6.2, 0],
      [6.2, -5.8],
      [0, -5.8],
    ],
  },
  {
    plotId: 'P2',
    purpose: 'waiting lounge, incident huddle, and dispatch queue',
    interior: OPS_INTERIOR,
    islandZ: OPS_ISLAND_Z,
    // Prove the three-part operations program rather than walking only to the
    // old single desk. The route enters and exits the glass room through its
    // central 1.5 m opening before crossing the clear aisle to dispatch.
    waypoints: [
      [4.2, 8],
      [3.5, 4.7],
      [6.7, -1.6],
      [6.7, -3.2],
      [6.7, -1.6],
      [1.8, -1.2],
      [-5.2, -4.8],
    ],
  },
  {
    plotId: 'P4',
    purpose: 'cost studio, live flow table, capacity station, and standing review point',
    interior: EXCHANGE_INTERIOR,
    islandZ: EXCHANGE_ISLAND_Z,
    // Enter and leave the west glass studio through its real doorway, use the
    // clear south edge of the central flow table, then reach both east-side
    // programs before reversing the same unobstructed route to the exit.
    waypoints: [
      [0, 6],
      [-9, 0.4],
      [-9, -1.6],
      [-9, -3.25],
      [-9, -1.6],
      [-9, 0.4],
      [0, 0],
      [0, -2.15],
      [4.5, 0],
      [7.6, -3.2],
      [9.05, -3.2],
      [11.7, -3.2],
    ],
  },
  {
    plotId: 'P5',
    purpose: 'paired build pods, test cell, and plugin bay',
    interior: WORKSHOP_INTERIOR,
    islandZ: WORKSHOP_ISLAND_Z,
    // Use both build pods, enter and leave the glass test cell through the
    // real central doorway, inspect its table/screen, then reach the plugin bay
    // before returning through the same unobstructed 2.4 m central spine.
    waypoints: [
      [0, 5.5],
      [-5.3, 4.8],
      [-5.3, 1.2],
      [-2.0, -2.5],
      [0, -2.6],
      [0, -4.4],
      [-1.8, -6.1],
      [0, -7.5],
      [1.8, -6.1],
      [0, -4.4],
      [0, -2.6],
      [6.0, -5.8],
      [6.0, 1.2],
      [0, 5.5],
    ],
  },
  {
    plotId: 'P6',
    purpose: 'conversation clusters, service bar, common project table, and quiet room',
    interior: CAFE_INTERIOR,
    islandZ: CAFE_ISLAND_Z,
    // Visit both social clusters, the service aisle, both sides of the shared
    // table, enter and leave the glass quiet room through its real doorway,
    // then return over the unobstructed central promenade to the exit.
    waypoints: [
      [0, 6],
      [-6.2, 5.5],
      [-8.2, 4.75],
      [-10.6, 4.75],
      [-10.4, -5.7],
      [-10.4, -0.8],
      [-7, -0.8],
      [-3, -4],
      [-3, -1.75],
      [0, -1.75],
      [3, -1.75],
      [3, -4],
      [8.7, 0.15],
      [8.7, -1.9],
      [8.7, -4.3],
      [10.7, -4.65],
      [8.7, -4.3],
      [8.7, -1.9],
      [8.7, 0.15],
      [0, 0.4],
      [0, 6],
    ],
  },
  {
    plotId: 'P7',
    purpose: 'reading bays, knowledge index, stack aisle, and research studio',
    interior: LIBRARY_INTERIOR,
    islandZ: LIBRARY_ISLAND_Z,
    // Traverse all three useful programs and one real 2.6 m stack aisle. The
    // route passes around (never through) the physical map and synthesis table.
    waypoints: [
      [-7.2, 10.4],
      [-4.2, 7.8],
      [-3.5, 6.2],
      [-3.5, 5.8],
      [-3.5, -8.5],
      [-2.8, -9.2],
      [-2.8, -10.3],
      [0, -11.7],
      [2.8, -10.3],
      [2.8, -9.2],
      [3.5, -8.5],
      [3.5, 5.8],
      [3.5, 6.2],
      [4.2, 7.8],
      [7.2, 10.4],
    ],
  },
  {
    plotId: 'P8',
    purpose: 'NOC, service aisles, world core, and utility review',
    interior: DATACENTRE_INTERIOR,
    islandZ: DATACENTRE_ISLAND_Z,
    waypoints: [
      [0, 9.8],
      [8.5, 9.8],
      [8.5, 8.7],
      [8.5, 5.2],
      [8.5, 8.7],
      [8.5, 9.8],
      [2.2, 9.8],
      [2.2, 2.6],
      [5.5, 2.6],
      [5.5, 0],
      [5.5, -3.3],
      [2.1, -3.8],
      [2.2, -6.5],
      [2.2, -8.6],
      [-2.2, -8.6],
      [-2.2, -6.5],
      [-2.2, -3.8],
      [-8.5, -3.3],
      [-8.5, 0],
      [-8.5, 2.6],
      [-2.2, 2.6],
      [-2.2, 8.7],
    ],
  },
]

export interface WorkplaceTraversalCheckpoint {
  plotId: WorkplaceTraversalSpec['plotId']
  purpose: string
  purposeReached: boolean
  exitReached: boolean
  grounded: boolean
  x: number
  y: number
  z: number
}

export interface WorkplaceTraversalArtifact {
  schema: 'arena.workplace-interior-traversal.v1'
  status: 'measured'
  capturedAt: string
  colliderCount: number
  frames: number
  minY: number
  maxY: number
  checkpoints: WorkplaceTraversalCheckpoint[]
  failures: string[]
  gate: {
    expectedRoomCount: 7
    roomCountPass: boolean
    purposePass: boolean
    exitPass: boolean
    groundedPass: boolean
    noFallPass: boolean
    passed: boolean
  }
  passed: boolean
}

interface WalkState {
  x: number
  y: number
  z: number
  grounded: boolean
}

interface WalkStats {
  frames: number
  minY: number
  maxY: number
  failures: string[]
}

const PLAYER_RADIUS = 0.3
const PLAYER_HEIGHT = 1.8
const WALK_STEP = 0.08
const TARGET_EPSILON = 0.05
const MAX_SEGMENT_FRAMES = 700

function walkSegment(
  state: WalkState,
  target: { x: number; z: number },
  colliders: readonly AABB[],
  stats: WalkStats,
  label: string,
): boolean {
  let previousDistance = Infinity
  let stalledFrames = 0
  for (let frame = 0; frame < MAX_SEGMENT_FRAMES; frame += 1) {
    const dx = target.x - state.x
    const dz = target.z - state.z
    const distance = Math.hypot(dx, dz)
    if (distance <= TARGET_EPSILON) return true
    const step = Math.min(WALK_STEP, distance)
    const result = moveAndResolveCollisions(
      state,
      { x: dx / distance * step, y: -0.05, z: dz / distance * step },
      PLAYER_RADIUS,
      PLAYER_HEIGHT,
      colliders,
      {
        wasGrounded: state.grounded,
        maxStepUp: MAX_STEP,
        maxStepDown: MAX_STEP,
      },
    )
    state.x = result.x
    state.y = result.y
    state.z = result.z
    state.grounded = result.grounded
    stats.frames += 1
    stats.minY = Math.min(stats.minY, state.y)
    stats.maxY = Math.max(stats.maxY, state.y)
    const nextDistance = Math.hypot(target.x - state.x, target.z - state.z)
    stalledFrames = nextDistance >= previousDistance - 1e-5
      ? stalledFrames + 1
      : 0
    previousDistance = nextDistance
    if (state.y < -0.5) {
      stats.failures.push(`${label}: fell below the room floor at y=${state.y.toFixed(3)}`)
      return false
    }
    if (stalledFrames >= 20) {
      stats.failures.push(`${label}: collision-blocked ${nextDistance.toFixed(3)}m from target`)
      return false
    }
  }
  stats.failures.push(`${label}: exceeded ${MAX_SEGMENT_FRAMES} movement frames`)
  return false
}

/** Walk spawn → useful purpose → exit in all seven non-HQ interiors. */
export function runWorkplaceTraversalAcceptance(
  colliders: readonly AABB[],
  capturedAt = new Date().toISOString(),
): WorkplaceTraversalArtifact {
  const stats: WalkStats = {
    frames: 0,
    minY: 0,
    maxY: 0,
    failures: [],
  }
  const checkpoints: WorkplaceTraversalCheckpoint[] = []
  for (const spec of WORKPLACE_TRAVERSAL_SPECS) {
    const state: WalkState = {
      x: spec.interior.spawn.x,
      y: 0,
      z: spec.interior.spawn.z,
      grounded: true,
    }
    let purposeReached = true
    for (const [[x, z], waypointIndex] of spec.waypoints.map(
      (waypoint, index) => [waypoint, index] as const,
    )) {
      purposeReached = purposeReached && walkSegment(
        state,
        { x, z: spec.islandZ + z },
        colliders,
        stats,
        `${spec.plotId} ${spec.purpose} waypoint ${waypointIndex + 1}`,
      )
      if (!purposeReached) break
    }
    let returnRouteReached = purposeReached
    for (const [x, z] of [...spec.waypoints].slice(0, -1).reverse()) {
      returnRouteReached = returnRouteReached && walkSegment(
        state,
        { x, z: spec.islandZ + z },
        colliders,
        stats,
        `${spec.plotId} return route`,
      )
      if (!returnRouteReached) break
    }
    const exitTarget = {
      x: (spec.interior.exitThreshold.minX + spec.interior.exitThreshold.maxX) / 2,
      z: spec.interior.exitThreshold.minZ + 0.35,
    }
    const exitReached = returnRouteReached && walkSegment(
      state,
      exitTarget,
      colliders,
      stats,
      `${spec.plotId} exit`,
    )
    checkpoints.push({
      plotId: spec.plotId,
      purpose: spec.purpose,
      purposeReached,
      exitReached,
      grounded: state.grounded,
      x: state.x,
      y: state.y,
      z: state.z,
    })
  }

  const roomCountPass = checkpoints.length === 7
  const purposePass = checkpoints.every((checkpoint) => checkpoint.purposeReached)
  const exitPass = checkpoints.every((checkpoint) => checkpoint.exitReached)
  const groundedPass = checkpoints.every((checkpoint) => checkpoint.grounded)
  const noFallPass = stats.minY >= -0.5
  const passed = roomCountPass && purposePass && exitPass && groundedPass &&
    noFallPass && stats.failures.length === 0
  return {
    schema: 'arena.workplace-interior-traversal.v1',
    status: 'measured',
    capturedAt,
    colliderCount: colliders.length,
    frames: stats.frames,
    minY: stats.minY,
    maxY: stats.maxY,
    checkpoints,
    failures: stats.failures,
    gate: {
      expectedRoomCount: 7,
      roomCountPass,
      purposePass,
      exitPass,
      groundedPass,
      noFallPass,
      passed,
    },
    passed,
  }
}
